/**
 * upload-state-db.ts
 *
 * INFRA-ADR-023 D7 / SRS-008 FR-5.2 / SDS-008 §7.2
 *
 * IndexedDB-backed upload state for resume after browser tab close.
 *
 * Schema:
 *   DB   = "backup-upload-state"
 *   Store= "uploads"
 *   Key  = file_hash (string)
 *   Value= UploadState
 */

interface UploadState {
  fileHash: string;
  fileName: string;
  fileSize: number;
  totalChunks: number;
  chunkSize: number;
  completedChunks: number[]; // ascending, deduped
  createdAt: string; // ISO
  lastUpdate: string; // ISO
  version: number;
}

const DB_NAME = 'backup-upload-state';
const DB_VERSION = 1;
const STORE_NAME = 'uploads';
const SCHEMA_VERSION = 1;

/** Open or upgrade the database. Returns an open IDBDatabase. */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'fileHash' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> {
  const db = await openDB();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const maybeReq = fn(store);
      tx.onerror = () => reject(tx.error);
      if (maybeReq instanceof IDBRequest) {
        maybeReq.onsuccess = () => resolve(maybeReq.result as T);
        maybeReq.onerror = () => reject(maybeReq.error);
      } else {
        Promise.resolve(maybeReq).then(resolve, reject);
      }
    });
  } finally {
    db.close();
  }
}

/** Load state for a file_hash. Returns null if missing. */
async function loadUploadState(fileHash: string): Promise<UploadState | null> {
  const v = await withStore('readonly', (store) => store.get(fileHash));
  if (!v) return null;
  const state = v as UploadState;
  if (state.version !== SCHEMA_VERSION) return null; // stale schema, treat as fresh
  return state;
}

/** Create or resume state. If existing with same size/chunks, returns existing. */
export async function initUploadState(
  fileHash: string,
  fileName: string,
  fileSize: number,
  totalChunks: number,
  chunkSize: number,
): Promise<UploadState> {
  const existing = await loadUploadState(fileHash);
  if (
    existing &&
    existing.fileSize === fileSize &&
    existing.totalChunks === totalChunks
  ) {
    return existing;
  }
  const state: UploadState = {
    fileHash,
    fileName,
    fileSize,
    totalChunks,
    chunkSize,
    completedChunks: [],
    createdAt: new Date().toISOString(),
    lastUpdate: new Date().toISOString(),
    version: SCHEMA_VERSION,
  };
  await withStore('readwrite', (store) => store.put(state));
  return state;
}

/** Mark chunk idx as complete (idempotent, sorted). */
export async function markChunkComplete(fileHash: string, chunkIndex: number): Promise<void> {
  const state = await loadUploadState(fileHash);
  if (!state) return;
  if (state.completedChunks.includes(chunkIndex)) return;
  state.completedChunks = [...state.completedChunks, chunkIndex].sort((a, b) => a - b);
  state.lastUpdate = new Date().toISOString();
  await withStore('readwrite', (store) => store.put(state));
}

/** Remove state (call after /upload/complete succeeds). */
export async function removeUploadState(fileHash: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(fileHash));
}
